import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { instanceToPlain } from 'class-transformer';
import { useAuthContext } from '../../contexts/AuthContext';
import { PersonProfile } from '../../entities';
import { Collections, db, functions } from '../../utils/firebase.config';
import { converterOf } from '../../utils/firebase.utils';
import DeleteProfile from './resources/delete-profile.resource';

export const useProfiles = (personId: string) => {
  const { user } = useAuthContext();

  const personsCollection = collection(db, Collections.Persons);
  const personDoc = doc(personsCollection, personId);
  const profilesCollection = collection(personDoc, Collections.Profiles).withConverter(converterOf(PersonProfile));

  const fetchAll = async (): Promise<PersonProfile[]> => {
    const resources = await getDocs(profilesCollection);

    return resources.docs.map((resource) => resource.data());
  };

  const update = async (profile: Partial<PersonProfile> & { id: string }): Promise<void> => {
    const profileDoc = doc(profilesCollection, profile.id);

    const instance = new PersonProfile({
      ...profile,
      condominiumId: profile.condominiumId ?? null,
      unitId: profile.unitId ?? null,
      modified: {
        at: new Date(),
        by: user?.uid!,
      },
    });

    return setDoc(profileDoc, instance, { merge: true });
  };

  const create = async (profile: Omit<PersonProfile, 'id'>): Promise<PersonProfile> => {
    const profileDoc = doc(profilesCollection);

    const newProfile = new PersonProfile({
      ...profile,
      id: profileDoc.id,
      created: {
        at: new Date(),
        by: user?.uid!,
      },
    });

    await setDoc(profileDoc, newProfile);

    return newProfile;
  };

  const deleteProfile = async (profileId: string): Promise<void> => {
    const deleteProfileAsManager = httpsCallable(functions, 'deleteProfileAsManager');

    const profileResource = new DeleteProfile({ personId, profileId});
    await deleteProfileAsManager(instanceToPlain(profileResource));
  };

  return { fetchAll, update, create, deleteProfile };
};

export default useProfiles;
